import React, { useState, useEffect, useMemo } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import './VideoTasks.css';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const formatValue = (value) => {
  if (value == null) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const STATUS_BADGE = {
  pending:    'badge-warning',
  queued:     'badge-warning',
  processing: 'badge-info',
  running:    'badge-info',
  completed:  'badge-success',
  done:       'badge-success',
  failed:     'badge-error',
  error:      'badge-error',
  cancelling: 'badge-warning',
};

const PAGE_SIZES = [25, 50, 100];
const STATUS_OPTIONS = ['', 'queued', 'processing', 'completed', 'failed', 'cancelling'];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
    pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const VideoTasks = () => {
  const { apiCall } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [debouncedTaskIdFilter, setDebouncedTaskIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showSlides, setShowSlides] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [oldTasksModal, setOldTasksModal] = useState(false);
  const [oldTasksDays, setOldTasksDays] = useState(30);
  const [oldTasksCustom, setOldTasksCustom] = useState('');
  const [oldTasksScope, setOldTasksScope] = useState('all');
  const [oldTasksUserId, setOldTasksUserId] = useState('');

  // 204-safe wrapper
  const apiCallNoBody = async (endpoint, options) => {
    try { return await apiCall(endpoint, options); }
    catch (err) { if (err instanceof SyntaxError) return null; throw err; }
  };

  const fetchTasks = async (page = currentPage, size = pageSize, q = debouncedTaskIdFilter, status = statusFilter, includeSlides = showSlides) => {
    setLoading(true);
    setError('');
    try {
      let url = `/admin/video-tasks?page=${page}&page_size=${size}&exclude_slides=${!includeSlides}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      const data = await apiCall(url);
      setTasks(data.tasks || []);
      // Support both envelope shapes: nested {pagination: {...}} or flat
      const pg = data.pagination || data;
      setTotal(pg.total || 0);
      setTotalPages(pg.total_pages || 1);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Debounce task ID filter
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedTaskIdFilter(taskIdFilter); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [taskIdFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter, showSlides); }, [currentPage, pageSize, debouncedTaskIdFilter, statusFilter, showSlides]);

  // Fetch all matching tasks (loop pages) — used by bulk-by-user and bulk-old-tasks
  const fetchAllMatching = async (filter = {}) => {
    const all = [];
    let page = 1;
    while (true) {
      const params = new URLSearchParams({ page, page_size: 100, exclude_slides: 'false', ...filter });
      const data = await apiCall(`/admin/video-tasks?${params}`);
      all.push(...(data.tasks || []));
      const pg = data.pagination || data;
      const totalPagesResp = pg.total_pages || 1;
      if (page >= totalPagesResp) break;
      page++;
    }
    return all;
  };

  const handleDeleteTask = async (taskId) => {
    setDeleteLoading(true);
    try {
      await apiCallNoBody(`/admin/video-tasks/${taskId}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      await fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter);
    } catch (err) {
      setError(`Failed to delete task: ${err.message}`);
      setDeleteConfirm(null);
    }
    setDeleteLoading(false);
  };

  const handleDeleteBulk = async (taskIds) => {
    setDeleteLoading(true);
    try {
      await Promise.all(taskIds.map((id) => apiCallNoBody(`/admin/video-tasks/${id}`, { method: 'DELETE' })));
      setSelectedTaskIds(new Set());
      setDeleteConfirm(null);
      await fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter);
    } catch (err) {
      setError(`Failed to delete tasks: ${err.message}`);
      setDeleteConfirm(null);
    }
    setDeleteLoading(false);
  };

  const handleDeleteByUser = async (userId) => {
    setDeleteLoading(true);
    try {
      const userTasks = await fetchAllMatching({ user_id: userId });
      await Promise.all(userTasks.map((t) => apiCallNoBody(`/admin/video-tasks/${t.task_id}`, { method: 'DELETE' })));
      setDeleteConfirm(null);
      await fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter);
    } catch (err) {
      setError(`Failed to delete tasks: ${err.message}`);
      setDeleteConfirm(null);
    }
    setDeleteLoading(false);
  };

  const handleDeleteOldTasks = async () => {
    const effectiveDays = oldTasksDays === 'custom' ? parseInt(oldTasksCustom, 10) : oldTasksDays;
    if (!effectiveDays || effectiveDays < 1) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - effectiveDays);
    setDeleteLoading(true);
    try {
      const filter = oldTasksScope === 'user' && oldTasksUserId.trim()
        ? { user_id: oldTasksUserId.trim() }
        : {};
      const allTasks = await fetchAllMatching(filter);
      const toDelete = allTasks.filter((t) => {
        const created = new Date(t.created_at);
        return !isNaN(created.getTime()) && created < cutoff;
      });
      await Promise.all(toDelete.map((t) => apiCallNoBody(`/admin/video-tasks/${t.task_id}`, { method: 'DELETE' })));
      setOldTasksModal(false);
      await fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter);
    } catch (err) {
      setError(`Failed to delete old tasks: ${err.message}`);
      setOldTasksModal(false);
    }
    setDeleteLoading(false);
  };

  // Old-tasks preview count — only computable against the currently loaded page (server-side filtering needed for full count)
  const oldTasksPreviewCount = useMemo(() => {
    if (!oldTasksModal) return 0;
    const effectiveDays = oldTasksDays === 'custom' ? parseInt(oldTasksCustom, 10) : oldTasksDays;
    if (!effectiveDays || effectiveDays < 1) return 0;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - effectiveDays);
    return tasks.filter((t) => {
      const created = new Date(t.created_at);
      if (isNaN(created.getTime())) return false;
      if (created >= cutoff) return false;
      if (oldTasksScope === 'user' && oldTasksUserId.trim()) {
        return t.user_id === oldTasksUserId.trim();
      }
      return true;
    }).length;
  }, [oldTasksModal, tasks, oldTasksDays, oldTasksCustom, oldTasksScope, oldTasksUserId]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator inactive">↕</span>;
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Client-side sort on current page (slide filtering is server-side via exclude_slides)
  const displayed = [...tasks]
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSelectTask = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const pageIds = displayed.map((t) => t.task_id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedTaskIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedTaskIds.has(id));

  const toggleSelectAllPage = () => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  if (loading && tasks.length === 0) return <Loader />;

  return (
    <div className="vt-page">
      <div className="page-header">
        <h2 className="page-title">Video Tasks ({total})</h2>
        <div className="vt-header-actions">
          {selectedTaskIds.size > 0 && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setDeleteConfirm({ type: 'bulk', taskIds: [...selectedTaskIds] })}
            >
              <DeleteSweepIcon sx={{ fontSize: '1rem' }} />
              Delete Selected ({selectedTaskIds.size})
            </button>
          )}
          <button onClick={() => fetchTasks(currentPage, pageSize, debouncedTaskIdFilter, statusFilter)} className="btn-icon" disabled={loading}>
            <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
          </button>
          <div className="vt-actions-menu-wrapper">
            <button
              onClick={() => setActionsMenuOpen((v) => !v)}
              className="btn-icon"
              title="More actions"
            >
              <MoreVertIcon sx={{ fontSize: '1.25rem' }} />
            </button>
            {actionsMenuOpen && (
              <>
                <div className="vt-menu-backdrop" onClick={() => setActionsMenuOpen(false)} />
                <div className="vt-actions-menu">
                  <button
                    className="vt-menu-item"
                    onClick={() => { setActionsMenuOpen(false); setOldTasksModal(true); }}
                  >
                    <AutoDeleteIcon sx={{ fontSize: '1.125rem' }} />
                    Delete Old Tasks
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="vt-filters">
        <div className="vt-filter-field">
          <div className="vt-filter-label-row">
            <label className="vt-filter-label">Search</label>
            <label className="vt-slides-toggle">
              <input
                type="checkbox"
                checked={showSlides}
                onChange={(e) => { setShowSlides(e.target.checked); setCurrentPage(1); }}
              />
              Show slides
            </label>
          </div>
          <div className="search-input-wrapper">
            <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
            <input
              type="text"
              placeholder="Search by task ID, email, or name..."
              value={taskIdFilter}
              onChange={(e) => setTaskIdFilter(e.target.value)}
              className="search-input"
            />
            {taskIdFilter && (
              <button onClick={() => setTaskIdFilter('')} className="clear-search-btn">
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </button>
            )}
          </div>
        </div>

        <div className="vt-filter-field" style={{ flex: '0 0 10rem' }}>
          <label className="vt-filter-label">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="detail-select"
            style={{ width: '100%', padding: '0.625rem 0.75rem', fontSize: '0.9375rem' }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s || 'All'}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-card">
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="th-checkbox">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                    onChange={toggleSelectAllPage}
                  />
                </th>
                <th className="th-sortable" onClick={() => handleSort('task_id')}>
                  Task ID <SortIndicator field="task_id" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_email')}>
                  Email <SortIndicator field="user_email" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_display_name')}>
                  Name <SortIndicator field="user_display_name" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('user_id')}>
                  User ID <SortIndicator field="user_id" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('status')}>
                  Status <SortIndicator field="status" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('created_at')}>
                  Created At <SortIndicator field="created_at" />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-results">
                    {(debouncedTaskIdFilter.trim() || statusFilter)
                      ? 'No matching tasks'
                      : 'No tasks found'}
                  </td>
                </tr>
              ) : (
                displayed.map((task) => (
                  <tr key={task.task_id} className="user-row">
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.task_id)}
                        onChange={() => toggleSelectTask(task.task_id)}
                      />
                    </td>
                    <td className="td-id" title={task.task_id}>
                      {task.video_url?.startsWith('https') ? (
                        <a
                          href={task.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="vt-task-link"
                        >
                          {task.task_id}
                        </a>
                      ) : (
                        task.task_id
                      )}
                    </td>
                    <td>{task.user_email || '—'}</td>
                    <td>{task.user_display_name || '—'}</td>
                    <td className="td-id" title={task.user_id}>{task.user_id || '—'}</td>
                    <td>
                      {task.status
                        ? <span className={`badge ${STATUS_BADGE[task.status?.toLowerCase()] || 'badge-gray'}`}>
                            {task.status}
                          </span>
                        : '—'}
                    </td>
                    <td className="td-date">{formatDate(task.created_at)}</td>
                    <td className="td-actions">
                      <button
                        className="btn-icon-sm"
                        title="View details"
                        onClick={() => setSelectedTask(task)}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm btn-icon-danger"
                        title="Delete task"
                        onClick={() => setDeleteConfirm({ type: 'task', taskId: task.task_id })}
                      >
                        <DeleteIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="rows-per-page">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <span className="pagination-info">
            {total === 0
              ? 'No results'
              : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, total)} of ${total}`}
          </span>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
              </button>
              {getPageNumbers(currentPage, totalPages).map((p) =>
                typeof p === 'string' ? (
                  <span key={p} className="page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`page-btn${p === currentPage ? ' active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="page-btn page-nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Task Details</h3>
              <button onClick={() => setSelectedTask(null)} className="btn-close">
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              {Object.entries(selectedTask).map(([key, value]) => (
                <div className="detail-row" key={key}>
                  <span className="detail-label">{key}:</span>
                  <span className="detail-value detail-value-sm">{formatValue(value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Old Tasks Modal */}
      {oldTasksModal && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setOldTasksModal(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Old Tasks</h3>
              <button onClick={() => setOldTasksModal(false)} className="btn-close" disabled={deleteLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="old-tasks-section">
                <label className="vt-filter-label">Delete tasks older than</label>
                <div className="old-tasks-days-row">
                  {[30, 60, 90].map((d) => (
                    <button
                      key={d}
                      className={`day-chip${oldTasksDays === d ? ' active' : ''}`}
                      onClick={() => setOldTasksDays(d)}
                    >
                      {d} days
                    </button>
                  ))}
                  <button
                    className={`day-chip${oldTasksDays === 'custom' ? ' active' : ''}`}
                    onClick={() => setOldTasksDays('custom')}
                  >
                    Custom
                  </button>
                  {oldTasksDays === 'custom' && (
                    <input
                      type="number"
                      min="1"
                      placeholder="Days"
                      value={oldTasksCustom}
                      onChange={(e) => setOldTasksCustom(e.target.value)}
                      className="custom-days-input"
                    />
                  )}
                </div>
              </div>

              <div className="old-tasks-section">
                <label className="vt-filter-label">Scope</label>
                <div className="old-tasks-scope-row">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="old-tasks-scope"
                      value="all"
                      checked={oldTasksScope === 'all'}
                      onChange={() => setOldTasksScope('all')}
                    />
                    All users
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="old-tasks-scope"
                      value="user"
                      checked={oldTasksScope === 'user'}
                      onChange={() => setOldTasksScope('user')}
                    />
                    Specific user
                  </label>
                </div>
                {oldTasksScope === 'user' && (
                  <input
                    type="text"
                    placeholder="Enter user ID..."
                    value={oldTasksUserId}
                    onChange={(e) => setOldTasksUserId(e.target.value)}
                    className="old-tasks-user-input"
                  />
                )}
              </div>

              <div className="old-tasks-preview">
                <span className="preview-count">{oldTasksPreviewCount}</span> matching task(s) visible on this page
              </div>
              <p className="confirm-subtext">
                The backend will be queried across all pages before deletion. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setOldTasksModal(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteOldTasks}
                disabled={
                  deleteLoading ||
                  (oldTasksDays === 'custom' && !oldTasksCustom)
                }
              >
                {deleteLoading ? 'Deleting...' : 'Delete Old Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Confirm Delete</h3>
            </div>
            <div className="modal-body">
              {deleteConfirm.type === 'task' ? (
                <>
                  <p className="confirm-text">
                    Delete task <strong className="user-id-highlight">{deleteConfirm.taskId}</strong>?
                  </p>
                  <p className="confirm-subtext">
                    This hard-deletes the DB row. The video blob in GCS is not touched. This action cannot be undone.
                  </p>
                </>
              ) : deleteConfirm.type === 'bulk' ? (
                <>
                  <p className="confirm-text">
                    Delete <strong className="user-id-highlight">{deleteConfirm.taskIds.length}</strong> selected task(s)?
                  </p>
                  <p className="confirm-subtext">
                    This hard-deletes the DB rows. Video blobs in GCS are not touched. This action cannot be undone.
                  </p>
                </>
              ) : (
                <>
                  <p className="confirm-text">
                    Delete all tasks for user <strong className="user-id-highlight">{deleteConfirm.userId}</strong>?
                  </p>
                  <p className="confirm-subtext">
                    All tasks for this user will be fetched across all pages and hard-deleted. This action cannot be undone.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() =>
                  deleteConfirm.type === 'task'
                    ? handleDeleteTask(deleteConfirm.taskId)
                    : deleteConfirm.type === 'bulk'
                      ? handleDeleteBulk(deleteConfirm.taskIds)
                      : handleDeleteByUser(deleteConfirm.userId)
                }
                disabled={deleteLoading}
              >
                {deleteLoading
                  ? 'Deleting...'
                  : deleteConfirm.type === 'task' ? 'Delete Task'
                    : deleteConfirm.type === 'bulk' ? `Delete ${deleteConfirm.taskIds.length} Task(s)`
                      : 'Delete All Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoTasks;
